import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CTASection } from "@/components/CTASection";
import { RelatedServices } from "@/components/RelatedServices";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Button } from "@/components/ui/button";
import { Phone, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import heroImage from "@/assets/concrete-contractor-totowa-nj.webp";
import stampedConcreteImage from "@/assets/stamped-concrete-vs-pavers-totowa-nj.webp";
import foundationsImage from "@/assets/concrete-foundation-prep-paramus-bergen-county-nj.webp";
import footingsImage from "@/assets/concrete-foundation-caldwell-nj.webp";
import drivewaysImage from "@/assets/concrete-driveway-lincoln-park-nj.webp";
import sidewalksImage from "@/assets/concrete-sidewalk-installation-wayne-nj.webp";
import patiosImage from "@/assets/stamped-concrete-orange-essex-county-nj.webp";
import repairImage from "@/assets/concrete-foundation-repair-woodland-park-nj.webp";

const sections = [
  {
    title: "Stamped Concrete Installation",
    highlight: "in North Jersey",
    content: [
      "Thinking about getting that stone or brick look without the price tag? Stamped concrete can get you there. It's decorative concrete that mimics natural materials like slate, flagstone, or brick. And it holds up well when it's done right.",
      "We see a lot of these projects across Bergen County and Passaic County. Patios, walkways, driveways, pool decks. Commercial storefronts too. Homeowners around here care about curb appeal, and stamped concrete gives you options without the cost of natural stone.",
      "Here's how the process works. The crew preps and grades the base, pours the slab, applies a color hardener, then stamps the pattern before it cures. The base prep matters more than most people think. North Jersey's freeze-thaw season can crack a slab fast if the ground underneath wasn't handled properly.",
      <>
        Sealing is the other piece. A good seal protects the color and the surface from salt, moisture, and UV. Most projects here fit into the spring-to-fall construction window, so timing matters when you're planning one out. Read our take on <Link to="/concrete-services/stamped-concrete" className="text-secondary hover:underline">stamped concrete installation</Link> and how it holds up through North Jersey winters.
      </>,
    ],
    link: "/concrete-services/stamped-concrete",
    linkText: "Learn more about stamped concrete installation",
    image: stampedConcreteImage,
    imageAlt: "Stamped concrete patio installation in Totowa, NJ",
  },
  {
    title: "Concrete Driveways",
    highlight: "in North Jersey",
    content: [
      "A concrete driveway is one of the most practical upgrades you can make to a property. It handles heavy vehicle traffic, stands up to North Jersey winters, and lasts decades when it's poured and finished correctly.",
      "We handle everything from removal of the old surface to grading, forming, reinforcement, and the pour itself. Proper drainage is built into every project. Standing water on a driveway leads to cracking and heaving, especially with the freeze-thaw cycles we get here.",
      <>
        Whether you're replacing a worn-out asphalt driveway or pouring a new one for a construction project, the process starts with understanding your lot's grade and soil conditions. That determines thickness, reinforcement, and joint placement. See what goes into <Link to="/concrete-services/driveways" className="text-secondary hover:underline">concrete driveway installation</Link> built for daily traffic.
      </>,
    ],
    link: "/concrete-services/driveways",
    linkText: "Learn more about concrete driveways",
    image: drivewaysImage,
    imageAlt: "Concrete driveway installation in Lincoln Park, NJ",
  },
  {
    title: "Concrete Sidewalks",
    highlight: "in North Jersey",
    content: [
      "Cracked or uneven sidewalks are a liability issue and a code violation in many North Jersey municipalities. Whether it's a residential walkway or a commercial frontage, we handle sidewalk installation and replacement from start to finish.",
      "The process includes demolition of the existing slab, regrading, forming, and pouring to the correct thickness and pitch for drainage. We work with local code requirements across Passaic, Bergen, Essex, and Morris counties.",
      <>
        Sidewalk projects often tie into larger improvements: new steps, a front walkway, or a full curb-to-door replacement. We coordinate the whole scope so everything matches and drains properly. Learn more about <Link to="/concrete-services/sidewalks" className="text-secondary hover:underline">concrete sidewalk installation</Link> for homes and businesses.
      </>,
    ],
    link: "/concrete-services/sidewalks",
    linkText: "Learn more about concrete sidewalks",
    image: sidewalksImage,
    imageAlt: "Concrete sidewalk installation in Wayne, NJ",
  },
  {
    title: "Concrete Patios",
    highlight: "in North Jersey",
    content: [
      "A well-built concrete patio gives you a flat, durable outdoor surface that works for everything from grilling to entertaining. With a stamped, brushed, or smooth finish, the choice depends on the look you're going for and how you plan to use the space.",
      "We grade and compact the base, set proper forms, and pour to the right thickness for your application. Expansion joints and drainage pitch are built in so the slab performs through North Jersey's seasonal temperature swings.",
      <>
        Concrete patios pair well with outdoor kitchens, fire pits, and retaining walls. If you're planning a backyard project with multiple elements, we can scope the whole thing together. Here's how we handle <Link to="/concrete-services/patios" className="text-secondary hover:underline">concrete patio installation</Link>, and when pavers make more sense.
      </>,
    ],
    link: "/concrete-services/patios",
    linkText: "Learn more about concrete patios",
    image: patiosImage,
    imageAlt: "Stamped concrete patio in Orange, Essex County, NJ",
  },
  {
    title: "Concrete Foundations",
    highlight: "in North Jersey",
    content: [
      "If you're building an addition, a garage, or a new structure, the foundation is where everything starts. Get it wrong and the rest of the project suffers.",
      "Foundation work follows a set process. Site excavation, formwork, rebar placement, and a properly graded pour. In North Jersey, local building codes require foundations to reach a specific frost line depth. We handle the permits and inspections as part of the job so you're not chasing paperwork.",
      "Soil conditions change a lot from one town to the next. Parts of Essex and Morris counties sit on clay-heavy ground. That kind of soil holds water and shifts with the seasons. It needs extra attention to drainage and compaction before anything gets poured.",
      <>
        General contractors and builders also bring us in as a foundation subcontractor on residential and light commercial projects. We've done enough of these to know what inspectors in this area look for and how to keep the job moving on schedule. See how we pour <Link to="/concrete-services/foundations" className="text-secondary hover:underline">concrete foundations</Link> for additions, garages and new builds.
      </>,
    ],
    link: "/concrete-services/foundations",
    linkText: "Learn more about concrete foundations",
    image: foundationsImage,
    imageAlt: "Concrete foundation preparation in Paramus, Bergen County, NJ",
  },
  {
    title: "Concrete Footings",
    highlight: "in North Jersey",
    content: [
      "Footings are what keep a structure from settling or shifting over time. Decks, retaining walls, fences, porches, small outbuildings. They all need proper support underneath.",
      "The idea is simple. Pier footings or spread footings transfer the weight of a structure down to stable ground below the frost line. The crew digs to the required depth, sets the forms, places rebar, and pours. If the footing is undersized or too shallow, you'll see cracking and movement down the road.",
      "A lot of homes in Passaic and Bergen counties sit on sloped lots. That makes footing depth and placement a bigger deal, especially for deck and retaining wall projects. You can't just guess at it. The grade of the lot and the soil type both factor into how deep and how wide the footings need to be.",
      <>
        Porch and stoop replacements are another common one. Older homes across North Jersey tend to need this work after 30 or 40 years. The original footings may have shifted, or they weren't poured deep enough to begin with. Either way, we can take a look and give you a straight answer on what's needed. Learn what goes into <Link to="/concrete-services/footings" className="text-secondary hover:underline">concrete footings</Link> for decks, porches and additions.
      </>,
    ],
    link: "/concrete-services/footings",
    linkText: "Learn more about concrete footings",
    image: footingsImage,
    imageAlt: "Concrete footings installation in Caldwell, NJ",
  },
  {
    title: "Concrete Repair",
    highlight: "in North Jersey",
    content: [
      "Concrete doesn't last forever, especially in North Jersey. Freeze-thaw cycles, salt exposure, and settling soils all take their toll. Cracks widen, surfaces spall, and what started as a small issue becomes a trip hazard or a structural concern.",
      "We handle repairs on driveways, sidewalks, patios, steps, and foundation walls. Depending on the scope, that can mean patching and resurfacing, full slab replacement, or mudjacking to correct a sunken section.",
      "Not every crack means the whole slab needs to come out. We'll assess the damage and let you know whether a repair makes sense or whether replacement is the better investment long-term.",
    ],
    link: "/concrete-services/repair",
    linkText: "Learn more about concrete repair",
    image: repairImage,
    imageAlt: "Concrete foundation repair in Woodland Park, NJ",
  },
];

export default function ConcreteServices() {
  return (
    <>
      <Helmet>
        <title>Concrete Contractor Totowa, NJ | Masonry Pro Construction</title>
        <meta name="description" content="Concrete contractor in North Jersey. Driveways, foundations, stamped concrete, and repairs built for local soil and NJ winters. Free estimates available." />
        <meta property="og:description" content="Concrete contractor in North Jersey. Driveways, foundations, stamped concrete, and repairs built for local soil and NJ winters. Free estimates available." />
        <meta property="og:title" content="Concrete Contractor Totowa, NJ | Masonry Pro Construction" />
        
        <meta property="og:image" content="https://masonry-pro.com/og/concrete-services.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="twitter:image" content="https://masonry-pro.com/og/concrete-services.jpg" />
      </Helmet>

      <Header />
      <Breadcrumb />

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden" style={{ backgroundColor: '#0d1017', paddingTop: '160px', paddingBottom: '80px' }}>
          <img src={heroImage} alt="Concrete construction services in North Jersey" width={1200} height={800} fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(13,16,23,0.45) 0%, rgba(13,16,23,0.65) 100%)' }} />
          <div className="container mx-auto px-4 text-center relative z-10">
            <h1 style={{ color: '#f5f5f5', fontSize: '3rem', fontWeight: 'bold', lineHeight: 1.1, marginBottom: '1.5rem', textShadow: '0 2px 8px rgba(0,0,0,0.9)' }}>
              <span style={{ color: '#d4a853', WebkitTextFillColor: '#d4a853' }}>Concrete Contractor</span> in Totowa, NJ Serving North Jersey
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.25rem', maxWidth: '48rem', margin: '0 auto 2rem', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
              Stamped concrete, driveways, sidewalks, patios, foundations, and repair, built by crews who understand North Jersey soil conditions and building codes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="secondary" size="xl" asChild>
                <a href="tel:+19737252395" className="flex items-center gap-2">
                  <Phone className="h-5 w-5" />
                  (973) 725-2395
                </a>
              </Button>
              <Button variant="goldOutline" size="xl" asChild>
                <a href="#contact">Get Free Estimate</a>
              </Button>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="py-16 md:py-20 bg-background">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Stamped Concrete, Driveways, Sidewalks, Patios, Foundations & <span className="text-gradient-gold">Repair</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              Concrete work in North Jersey can take a beating. Freeze-thaw cycles, heavy rain, and clay soils all test what lies beneath the surface. If the base isn't right, it shows within a few years. Whether you're pouring a new patio, adding onto your home, or setting footings for a deck, we can walk you through what's involved and what to expect. Our crew has worked across North Jersey long enough to know what the soil and weather demand from a concrete pour. Concrete sits alongside brick, block, and stone in the work we take on as a <Link to="/" className="text-secondary hover:underline">masonry contractor serving North Jersey</Link>.
            </p>
            <p className="text-lg text-muted-foreground mt-4">
              If your existing slabs are cracked, sunken, or flaking instead, our <Link to="/concrete-services/repair" className="text-secondary hover:underline">concrete repair</Link> crew can figure out what's happening under the surface before it gets worse.
            </p>
          </div>
        </section>

        {/* Service Sections — alternating image/content */}
        {sections.map((section, index) => {
          const isEven = index % 2 === 0;
          return (
            <section
              key={section.title}
              className={`py-16 md:py-20 ${index % 2 === 0 ? "bg-muted/30" : "bg-background"}`}
            >
              <div className="container mx-auto px-4">
                <div
                  className={`max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ${
                    !isEven ? "lg:[direction:rtl]" : ""
                  }`}
                >
                  {/* Content */}
                  <div className={!isEven ? "lg:[direction:ltr]" : ""}>
                    <h2 className="text-3xl md:text-4xl font-bold mb-6">
                      {section.title} <span className="text-gradient-gold">{section.highlight}</span>
                    </h2>
                    <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
                      {section.content.map((p, i) => (
                        <p key={i}>{p}</p>
                      ))}
                    </div>
                    <Link
                      to={section.link}
                      className="inline-flex items-center gap-2 text-secondary font-medium hover:gap-3 transition-all duration-300 mt-4"
                    >
                      {section.linkText} <ArrowRight className="h-5 w-5" />
                    </Link>
                  </div>

                  {/* Image */}
                  <div
                    className={`overflow-hidden rounded-xl border border-border ${
                      !isEven ? "lg:[direction:ltr]" : ""
                    }`}
                  >
                    <img
                      src={section.image}
                      alt={section.imageAlt}
                      className="w-full h-auto aspect-[4/3] object-cover"
                      loading="lazy"
                    />
                  </div>
                </div>
              </div>
            </section>
          );
        })}

        <RelatedServices currentCategory="concrete" />

        {/* CTA Section */}
        <CTASection serviceName="concrete" heading="Need a" highlightedHeading="Concrete Estimate?" />
      </main>

      <Footer />
    </>
  );
}
